import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utils/cn";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

interface MonthYearPickerProps {
  isOpen: boolean;
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  triggerRect?: DOMRect | null;
}

/**
 * Modal month/year picker matching Blazor GRCReportDashboard calendar.
 * Shows current year with month selection; allows navigating years.
 */
export function MonthYearPicker({
  isOpen,
  currentDate,
  onDateSelect,
  onClose,
  triggerRect
}: MonthYearPickerProps) {
  const [displayYear, setDisplayYear] = useState(currentDate.getFullYear());
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayYear(currentDate.getFullYear());
  }, [currentDate]);

  useEffect(() => {
    if (!isOpen) return;

    // function handleClickOutside(event: MouseEvent) {
    //   if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
    //     onClose();
    //   }
    // }

    // document.addEventListener("click", handleClickOutside);
    // return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(displayYear, monthIndex, 1);
    onDateSelect(newDate);
    onClose();
  };

  // Calculate position below trigger
  const positionStyle: React.CSSProperties = triggerRect
    ? {
        top: `${triggerRect.bottom + 8}px`,
        left: `${triggerRect.left}px`,
      }
    : {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} role="presentation" />
      <div
        ref={pickerRef}
        className="fixed z-50 w-80 rounded-none border border-slate-300 bg-white p-3 shadow-lg"
        style={positionStyle}
        role="dialog"
        aria-label="Select month and year"
        aria-modal="true"
      >
        {/* Header with year navigation */}
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setDisplayYear(displayYear - 1)}
            aria-label="Previous year"
            className="flex h-8 w-8 items-center justify-center rounded-none hover:bg-slate-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <span className="text-center text-lg font-semibold text-slate-900">
            {displayYear}
          </span>

          <button
            type="button"
            onClick={() => setDisplayYear(displayYear + 1)}
            aria-label="Next year"
            className="flex h-8 w-8 items-center justify-center rounded-none hover:bg-slate-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((month, index) => {
            const isCurrentMonth =
              displayYear === currentDate.getFullYear() &&
              index === currentDate.getMonth();

            return (
              <button
                key={month}
                type="button"
                onClick={() => handleMonthSelect(index)}
                className={cn(
                  "rounded-none px-3 py-2 text-sm font-medium transition-colors",
                  isCurrentMonth
                    ? "border border-slate-900 bg-slate-900 text-white"
                    : "border border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                )}
              >
                {month}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
